import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Input } from "@twilio-paste/core/input";
import { Text } from "@twilio-paste/core/text";
import { FormEvent, useEffect, useRef, useState } from "react";
import log from "loglevel";
import { useDispatch, useSelector } from "react-redux";

import { Flex } from "@twilio-paste/core";
import type { Conversation } from "@twilio/conversations";
import { SendIcon } from "@twilio-paste/icons/esm/SendIcon";
import { useTheme } from "@twilio-paste/theme";
import { Token } from "../definitions";
import { notifications } from "../notifications";
import { sessionDataHandler } from "../sessionDataHandler";
import {
  addNotification,
  changeEngagementPhase,
  updatePendingSession,
  updatePreEngagementData,
} from "../store/actions/genericActions";
import { initSession } from "../store/actions/initActions";
import { AppState, EngagementPhase } from "../store/definitions";
import type { AppDispatch } from "../store/store";
import Dots from "./Dots";
import { Header } from "./Header";
import { NotificationBar } from "./NotificationBar";
import "./styles/input.module.css";
import {
  conversationStyles,
  fieldStyles,
  formStyles,
  writeHereStyles,
} from "./styles/PreEngagementFormPhase.styles";
import startMsg from "../definitions/startMsg";

export const PreEngagementFormPhase = () => {
  const { name, email, query } =
    useSelector((state: AppState) => state.session.preEngagementData) || {};
  const currentPhase = useSelector(
    (state: AppState) => state.session.currentPhase
  );
  const pendingSession = useSelector(
    (state: AppState) => state.session.pendingSession
  );
  const [text, setText] = useState(query);
  const isSubmittingRef = useRef(false);
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  // The store starts in Loading while a stored session is resumed, and stays there while a submit is pending.
  const isLoading = currentPhase === EngagementPhase.Loading;

  // Both a failed request and a session that cannot be loaded bring the phase back to the form.
  useEffect(() => {
    if (currentPhase === EngagementPhase.PreEngagementForm) {
      isSubmittingRef.current = false;
    }
  }, [currentPhase]);

  const canSubmit = () =>
    !isSubmittingRef.current && !isLoading && Boolean(query?.trim());

  const getReusableSession = () =>
    pendingSession &&
    Date.now() < new Date(pendingSession.session.expiration).getTime()
      ? pendingSession
      : undefined;

  // A reused session that fails again is dropped, so the next retry creates a new one.
  const keepPendingSession = (session: Token, wasReused: boolean) => {
    dispatch(
      updatePendingSession(wasReused ? undefined : { session, query: query ?? "" })
    );
  };

  const showFormAgain = (errorMessage: string) => {
    dispatch(
      addNotification(
        notifications.failedToInitSessionNotification(errorMessage)
      )
    );
    setText(query);
    dispatch(
      changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm })
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // The ref blocks re-entry within the same tick, before the Loading phase re-renders.
    if (!canSubmit()) {
      return;
    }
    isSubmittingRef.current = true;
    dispatch(changeEngagementPhase({ phase: EngagementPhase.Loading }));

    const reusableSession = getReusableSession();
    let session: Token;
    if (reusableSession) {
      session = reusableSession.session;
    } else {
      try {
        session = await sessionDataHandler.fetchAndStoreNewSession({
          formData: {
            friendlyName: name,
            email,
            query,
          },
        });
      } catch (err) {
        showFormAgain((err as Error).message);
        return;
      }
    }

    let conversation: Conversation | null;
    try {
      conversation = await dispatch(
        initSession({
          token: session.token,
          conversationSid: session.conversationSid,
        })
      );
    } catch {
      keepPendingSession(session, Boolean(reusableSession));
      showFormAgain("Couldn't load conversation");
      return;
    }
    if (!conversation) {
      // initSession has already notified the error and brought the form back.
      keepPendingSession(session, Boolean(reusableSession));
      setText(query);
      return;
    }

    dispatch(updatePendingSession());
    // The reused conversation was started with the previous query: the edited one is sent as a new message.
    if (reusableSession && query?.trim() && reusableSession.query.trim() !== query.trim()) {
      try {
        await conversation.sendMessage(query);
      } catch (err) {
        log.error("Couldn't send the edited query", err);
        dispatch(addNotification(notifications.failedToSendQueryNotification()));
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!canSubmit()) {
        return;
      }
      setText("");
      handleSubmit(e);
    }
  };

  return (
    <>
      <Header />
      <NotificationBar />
      <Box
        as="form"
        data-test="pre-engagement-chat-form"
        onSubmit={handleSubmit}
        {...formStyles}
      >
        <Box {...fieldStyles}>
          <Text
            as="h6"
            marginBottom="space40"
            textAlign="center"
            fontSize="fontSize20"
            fontWeight="fontWeightLight"
          >
            {new Date().toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
            })}
          </Text>
          <Flex display="inline-flex" marginBottom="space70">
            <img
              width="32px"
              src="https://storage-platform-prod.s3.amazonaws.com/logos/6785/logo.png?cb=15813865"
              style={{
                marginRight: "16px",
                marginBottom: "10px",
                alignSelf: "end",
              }}
            />
            <Box {...conversationStyles}>
              <Text as="p">{startMsg.body}</Text>
            </Box>
          </Flex>
          {currentPhase === EngagementPhase.Loading && <Dots />}
          <Box {...writeHereStyles}>
            <Input
              type="text"
              placeholder="Scrivi qui"
              name="query"
              element="write-here"
              data-test="pre-engagement-chat-form-query-textarea"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                dispatch(updatePreEngagementData({ query: e.target.value }));
              }}
              onKeyDown={handleKeyPress}
              disabled={isLoading}
              required
            />
            <Button variant="link" type="submit" disabled={isLoading}>
              <SendIcon
                color={theme.backgroundColors.colorBackgroundPrimary}
                decorative={false}
                title="Send message"
                size="sizeIcon30"
              />
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};
